"use client";

import { ChevronLeft, ChevronRight, SlidersHorizontal } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useSession } from "next-auth/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { heroSlides as defaultHeroSlides } from "@/lib/data";
import type { Promotion } from "@/types";

export function HeroCarousel({ slides }: { slides?: Promotion[] }) {
  const { data: session } = useSession();
  const isAdmin = session?.user?.role === "ADMIN" || session?.user?.role === "ADMIN1";

  const activeSlides =
    slides && slides.length > 0
      ? slides
      : defaultHeroSlides.map((s, idx) => ({
          id: `fallback-${idx}`,
          eyebrow: s.eyebrow,
          title: s.title,
          description: s.description,
          image: s.image,
          cta: s.cta,
          link: s.link || "/search",
          isActive: true,
          sortOrder: idx,
        }));

  // Ensure continuous scrollable cards
  const displayCards = activeSlides.length < 8 ? [...activeSlides, ...activeSlides] : activeSlides;

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  }, []);

  useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [checkScroll]);

  function scroll(direction: "left" | "right") {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = 230;
    const distance = direction === "left" ? -cardWidth * 2 : cardWidth * 2;
    el.scrollBy({ left: distance, behavior: "smooth" });
  }

  return (
    <section className="relative w-full overflow-hidden bg-[#eaeded] py-3 sm:py-4 dark:bg-slate-950">
      <div className="relative mx-auto max-w-[1500px] px-3 sm:px-4">
        {isAdmin && (
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">
              Storefront Hero Banner Carousel
            </span>
            <Link
              href="/admin/promotions"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white/95 px-2.5 py-1 text-[11px] font-bold text-slate-800 shadow-xs transition hover:border-amazon-orange hover:bg-white hover:text-amazon-orange dark:border-white/10 dark:bg-slate-900/90 dark:text-slate-200"
              title="Configure promotion cards in Admin"
            >
              <SlidersHorizontal className="h-3 w-3 text-amazon-orange" />
              <span>Manage Banners (Admin)</span>
            </Link>
          </div>
        )}

        {/* Horizontal Card Scroll Track */}
        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-3 overflow-x-auto scroll-smooth py-1 no-scrollbar sm:gap-3.5"
        >
          {displayCards.map((slide, index) => {
            const hasPreformattedImage = slide.image.startsWith("/promotions/");

            return (
              <Link
                key={`${slide.id}-${index}`}
                href={slide.link || "/search"}
                className="group relative w-[155px] sm:w-[185px] md:w-[215px] shrink-0 overflow-hidden rounded-[14px] sm:rounded-[18px] bg-white shadow-xs transition-all duration-300 hover:-translate-y-1 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-amazon-orange dark:bg-slate-900"
                style={{ aspectRatio: "196 / 316" }}
              >
                <div className="relative h-full w-full">
                  <Image
                    src={slide.image}
                    alt={slide.title}
                    fill
                    sizes="(max-width: 640px) 155px, (max-width: 768px) 185px, 215px"
                    className="object-cover"
                    priority={index < 5}
                  />

                  {/* Overlay text for products or custom banners without baked-in text */}
                  {!hasPreformattedImage && (
                    <div className="absolute inset-0 flex flex-col justify-between p-2.5 sm:p-3.5 bg-gradient-to-t from-black/85 via-black/35 to-black/60 text-white">
                      <div>
                        {slide.eyebrow && (
                          <span className="inline-block rounded-md bg-amazon-orange px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-white shadow-xs">
                            {slide.eyebrow}
                          </span>
                        )}
                        <h3 className="mt-1 sm:mt-1.5 line-clamp-2 text-xs sm:text-sm font-black leading-snug tracking-tight text-white drop-shadow-md">
                          {slide.title}
                        </h3>
                      </div>
                      <div className="space-y-1 sm:space-y-1.5">
                        {slide.description && (
                          <p className="line-clamp-1 text-[10px] sm:text-[11px] font-semibold text-amber-300">
                            {slide.description}
                          </p>
                        )}
                        {slide.cta && (
                          <span className="inline-flex items-center rounded-full bg-amazon-gold px-2 sm:px-2.5 py-0.5 sm:py-1 text-[10px] sm:text-[11px] font-bold text-slate-950 shadow-sm transition group-hover:bg-amber-400">
                            {slide.cta}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>

        {/* Left Arrow Button */}
        {canScrollLeft && (
          <button
            type="button"
            onClick={() => scroll("left")}
            className="absolute left-1 top-1/2 -translate-y-1/2 z-20 hidden sm:flex h-16 w-8 items-center justify-center rounded-r-md border border-l-0 border-slate-300/80 bg-white/95 text-slate-800 shadow-md transition hover:bg-white hover:text-black dark:border-white/10 dark:bg-slate-900/95 dark:text-white"
            aria-label="Previous promotional items"
          >
            <ChevronLeft className="h-5 w-5 stroke-[2.5]" />
          </button>
        )}

        {/* Right Arrow Button (matches user screenshot exactly) */}
        {canScrollRight && (
          <button
            type="button"
            onClick={() => scroll("right")}
            className="absolute right-1 top-1/2 -translate-y-1/2 z-20 hidden sm:flex h-16 w-8 items-center justify-center rounded-l-md border border-r-0 border-slate-300/80 bg-white/95 text-slate-800 shadow-md transition hover:bg-white hover:text-black dark:border-white/10 dark:bg-slate-900/95 dark:text-white"
            aria-label="Next promotional items"
          >
            <ChevronRight className="h-5 w-5 stroke-[2.5]" />
          </button>
        )}
      </div>
    </section>
  );
}
